import { MIN_WIDTH } from './columnWidthCalculator';

// Shrinks columns to fit within the container width while maintaining proportions
export function shrinkColumnsToFit(widths, containerWidth) {
    const totalWidth = calculateTotalWidth(widths);
    const excessWidth = totalWidth - containerWidth;

    // No shrinking needed
    if (excessWidth <= 0) {
        return widths;
    }

    // Create a mutable copy
    const result = { ...widths };

    // Calculate how much each column can shrink
    const shrinkableAmounts = calculateShrinkableAmounts(result);

    // Distribute the excess width proportionally to column sizes
    distributeShrinkage(result, shrinkableAmounts, excessWidth);

    // Ensure all widths are valid integers
    return normalizeWidths(result);
}

function calculateTotalWidth(widths) {
    return Object.values(widths).reduce((sum, width) => sum + width, 0);
}

function calculateShrinkableAmounts(widths) {
    const amounts = {};

    Object.entries(widths).forEach(([key, width]) => {
        amounts[key] = width - MIN_WIDTH;
    });

    return amounts;
}

// Distributes shrinkage across columns using quadratic weighting
// (larger columns shrink more to maintain better proportions)
function distributeShrinkage(widths, shrinkableAmounts, totalShrinkage) {
    const weights = calculateWeights(widths, shrinkableAmounts);
    const totalWeight = calculateTotalWeight(weights);
    const distributedAmount = applyWeightedShrinkage(widths, shrinkableAmounts, weights, totalWeight, totalShrinkage);

    // Handle any remaining shrinkage due to rounding or constraints
    const remainingShrinkage = totalShrinkage - distributedAmount;
    if (remainingShrinkage > 0.01) {
        distributeRemainingShrinkage(widths, remainingShrinkage);
    }
}

// Calculates quadratic weights for each column (larger columns get more weight)
function calculateWeights(widths, shrinkableAmounts) {
    const weights = {};

    Object.entries(widths).forEach(([key, width]) => {
        if (shrinkableAmounts[key] > 0) {
            weights[key] = width * width;
        }
    });

    return weights;
}

function calculateTotalWeight(weights) {
    return Object.values(weights).reduce((sum, weight) => sum + weight, 0);
}

// Applies weighted shrinkage to each column and returns total amount distributed
function applyWeightedShrinkage(widths, shrinkableAmounts, weights, totalWeight, totalShrinkage) {
    let distributedShrinkage = 0;

    Object.entries(weights).forEach(([key, weight]) => {
        const proportion = weight / totalWeight;
        const idealShrinkage = totalShrinkage * proportion;
        const actualShrinkage = Math.min(idealShrinkage, shrinkableAmounts[key]);

        widths[key] -= actualShrinkage;
        distributedShrinkage += actualShrinkage;
    });

    return distributedShrinkage;
}

// Distributes any remaining shrinkage evenly among columns that can still shrink
function distributeRemainingShrinkage(widths, remaining) {
    const shrinkableColumns = findShrinkableColumns(widths);

    if (shrinkableColumns.length === 0) return;

    const perColumn = remaining / shrinkableColumns.length;

    shrinkableColumns.forEach(key => {
        const maxShrink = widths[key] - MIN_WIDTH;
        const actualShrink = Math.min(perColumn, maxShrink);
        widths[key] -= actualShrink;
    });
}

function findShrinkableColumns(widths) {
    return Object.entries(widths)
        .filter(([, width]) => width > MIN_WIDTH)
        .map(([key]) => key);
}

// Ensures all widths are valid integers at or above minimum
function normalizeWidths(widths) {
    const normalized = {};

    Object.entries(widths).forEach(([key, width]) => {
        normalized[key] = Math.max(MIN_WIDTH, Math.floor(width));
    });

    return normalized;
}

export default {
    shrinkColumnsToFit
};
